import { findSectionByTitle } from "@/common/steps";
import { ROUTE_NAME } from "@/constants/route.constants";
import router from "@/router";

export const steps = {
  namespaced: true,
  state: {
    currentStep: 1,
    startingStep: 1,
    getQuotesStep: 2,
    pricesStep: 3,
    terminusSteps: [3, 6],
    applicationStartingStep: 4,
    paymentDetailStep: 5,
    quotationSteps: 3,
    applicationSteps: 3,
    lastStepEdited: "",
  },
  actions: {
    handleNext({ getters, dispatch, commit }, { section, ref }) {
      // if form is not valid scroll to first input that is not valid
      if (!ref.validate() && ref.inputs?.length > 0) {
        const firstErrorInput = ref.inputs.find((e) => !e.valid);
        if (firstErrorInput) {
          firstErrorInput.$el.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
        return;
      }
      if (section.SectionNumber < getters.totalSteps) {
        commit("SET_STEP", section.SectionNumber + 1);
        dispatch("refreshSession");
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
    },
    handleNextWithoutRef({ getters, dispatch, commit }, { section }) {
      if (section.SectionNumber < getters.totalSteps) {
        commit("SET_STEP", section.SectionNumber + 1);
        dispatch("refreshSession");
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
    },
    handleBack({ state, dispatch, commit }, { section }) {
      // Decrement the currentStep, if possible.
      if (section.SectionNumber > state.startingStep)
        commit("SET_STEP", section.SectionNumber - 1);

      // Do effects for special cases
      if (section.SectionNumber === state.startingStep) {
        // Going back from Step 1 - go to root (select cover type)
        router.push({ name: ROUTE_NAME.HOME.MAIN });
      } else if (section.SectionNumber === state.pricesStep) {
        // Going back from Prices to change some answers - clone the quote
        dispatch("questions/cloneQuote", null, { root: true });
      } else if (section.SectionNumber === state.applicationStartingStep) {
        // Going back from Application Qs to Prices
        router.push({ path: "/quotation" });
      }

      dispatch("refreshSession");
    },
    handleBackToYourQuote({ commit, dispatch }, payload) {
      commit("SET_STEP", payload);
      router.push({ path: "/quotation" });
      dispatch("refreshSession");
    },
    handleResetSteps({ state, dispatch, commit }) {
      commit("SET_STEP", state.startingStep);
      dispatch("refreshSession");
    },
    handleGoStep({ dispatch, commit }, payload) {
      commit("SET_STEP", payload);
      dispatch("refreshSession");
    },
    handleGoToLastEdited({ commit, rootGetters }, lastEditedSectionTitle) {
      const matchingStep = findSectionByTitle(
        rootGetters["questions/allSections"],
        lastEditedSectionTitle
      );
      if (matchingStep) {
        commit("SET_STEP", matchingStep.SectionNumber);
      }
    },
    handleGoApplication({ state, dispatch, commit }) {
      commit("SET_STEP", state.applicationStartingStep);
      dispatch("refreshSession");
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    },
    setLastStepEdited({ commit }, sectionTitle) {
      // Set the name of the last edited section
      commit("SET_LAST_STEP_EDITED", sectionTitle);
    },
    refreshSession({ dispatch }) {
      // Local proxy for the refreshSession action in questions module
      dispatch("questions/refreshSession", null, { root: true });
    },
    /**
     * Initialize step module with sections from question.json
     * @param {Array<{SectionNumber: number, IsGetQuoteStep: boolean?, IsGetPriceStep: boolean?}>} questionSections array of question sections
     */
    initializeQuotationSteps({ state }, questionSections) {
      if (!questionSections) {
        return;
      }
      for (const section of questionSections) {
        if (section.IsGetQuoteStep) {
          state.getQuotesStep = section.SectionNumber;
        } else if (section.IsGetPriceStep) {
          state.pricesStep = section.SectionNumber;
        }
      }

      state.quotationSteps = questionSections.length;
    },
    /**
     * Initialize step module with sections from question.json
     * @param {Array<{SectionNumber: number, IsApplicationStartingStep: boolean?, IsPaymentDetailStep: boolean?}>} applicationSections array of question sections
     */
    initializeApplicationSteps({ state }, applicationSections) {
      if (!applicationSections) {
        return;
      }
      for (const section of applicationSections) {
        if (section.IsApplicationStartingStep) {
          state.applicationStartingStep = section.SectionNumber;
        } else if (section.IsPaymentDetailStep) {
          state.paymentDetailStep = section.SectionNumber;
        }
      }

      state.applicationSteps = applicationSections.length;
    },
  },
  getters: {
    totalSteps: (state) => {
      return (state.applicationSteps ?? 3) + (state.quotationSteps ?? 3);
    },
    getApplicationStartingStep: (state) => state.applicationStartingStep,
    getGetQuotesStep: (state) => state.getQuotesStep,
    getPricesStep: (state) => state.pricesStep,
    getPaymentDetailStep: (state) => state.paymentDetailStep,
    getCurrentStep: (state) => state.currentStep,
    isAfterPriceStep: (state) => state.currentStep > state.pricesStep,
  },
  mutations: {
    SET_STEP(state, newStep) {
      state.currentStep = newStep;
    },
    SET_LAST_STEP_EDITED(state, sectionTitle) {
      // Since quote module and application module both send their last sectionTitle,
      // sometimes the one from app module will be blank, if we're still in quote stage
      if (!sectionTitle) return;
      state.lastStepEdited = sectionTitle;
    },
  },
};
